import { FormLabel } from '@material-ui/core';
import { HighlightOffRounded as RoundedIcon } from '@material-ui/icons';
import cx from 'classnames';
import { debounce } from 'lodash';
import {
  ChangeEvent,
  CSSProperties,
  forwardRef,
  ForwardRefRenderFunction,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { t } from '@core/i18n';
import { withStyles, WithStyles } from '@core/theme/utils/with-styles';
import { Ellipsis } from '@shared/components/ellipsis';
import { Flex } from '@shared/components/flex';
import { Loading } from '@shared/components/loading';
import { TextField, TextFieldProps } from '@shared/components/text-field';

import { defaultAbsoluteValue, styles } from './Autocomplete.styles';

export interface AutocompleteRef extends HTMLInputElement {
  clear: () => void;
}

export interface AutocompleteProps extends WithStyles<typeof styles> {
  action?: React.ReactNode;
  actionRow?: JSX.Element;
  clearAfterSelect?: boolean;
  disabled?: boolean;
  dropUp?: boolean;
  error?: boolean;
  hideEmpty?: boolean;
  inputProps?: Omit<TextFieldProps, 'classes' | 'disabled' | 'error'>;
  label?: ReactNode | string;
  list: any[];
  loading: boolean;
  loadingText?: string;
  minSearchLength?: number;
  priorityList?: any[];
  priorityListWithFocus?: boolean;
  searchValue: string;
  withClearIcon?: boolean;
  withListUpdate?: boolean;
  withSearchIcon?: boolean;
  onClearList: (clickToClearIcon?: boolean) => void;
  onGetList: (search: string) => void;
  onSearchValueChange: (search: string) => void;
  onSelect: (item: any) => void;
  renderOption: (option: any) => JSX.Element | string;
}

const AutocompleteComponent: ForwardRefRenderFunction<AutocompleteRef, AutocompleteProps> = (
  {
    action,
    actionRow,
    classes,
    clearAfterSelect,
    disabled = false,
    dropUp = false,
    error = false,
    hideEmpty = false,
    inputProps,
    label,
    list,
    loading,
    loadingText,
    minSearchLength = 1,
    priorityList,
    priorityListWithFocus = false,
    searchValue,
    withClearIcon = false,
    withListUpdate = false,
    withSearchIcon = false,
    onClearList,
    onGetList,
    onSearchValueChange,
    onSelect,
    renderOption,
  },
  ref
) => {
  const debouncedSearchRef = useRef(
    debounce((search: string) => {
      if (!search) {
        return;
      }

      onGetList(search);
      setTyping(false);
    }, 500)
  );
  const mountedRef = useRef(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);

  useImperativeHandle<HTMLElement, AutocompleteRef>(ref, () => ({
    ...(textFieldRef.current as HTMLInputElement),
    clear: () => handleClearSearch(),
    focus: (options) => {
      textFieldRef.current?.focus(options);
      setOpen(true);
    },
  }));

  const [prevList, setPrevList] = useState<any[]>(list);
  const [typing, setTyping] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (withListUpdate && list !== prevList) {
      setPrevList(list);
      setOpen(true);
    }
  }, [list]);

  // Fetch the list on init if we've auto focused and there is a valid initial search
  useEffect(() => {
    const trimmedValue = searchValue.trim();

    if (inputProps?.autoFocus && trimmedValue.length >= minSearchLength) {
      onGetList(trimmedValue);
    }
  }, []);

  const handleSearchChange = async (e: ChangeEvent<HTMLInputElement>) => {
    onSearchValueChange(e.target.value);

    if (e.target.value === '') {
      debouncedSearchRef.current.cancel();
      onClearList();
      return;
    }

    const trimmedValue = e.target.value.trim();

    if (trimmedValue.length >= minSearchLength) {
      setTyping(true);
      debouncedSearchRef.current(trimmedValue);
    }
  };

  useEffect(() => {
    return () => debouncedSearchRef.current.cancel();
  }, [debouncedSearchRef.current]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onSelect(e);

      setOpen(false);

      if (clearAfterSelect) {
        onClearList();
      }
    },
    [clearAfterSelect, onClearList, onSelect]
  );

  const handleClearSearch = () => {
    onSearchValueChange('');

    onClearList(true);
  };

  const listAbsoluteStyles: CSSProperties = useMemo(() => {
    const value = searchContainerRef.current?.clientHeight || defaultAbsoluteValue;

    if (dropUp) {
      return {
        bottom: value,
      };
    }

    return {
      top: value,
    };
  }, [dropUp, searchContainerRef.current?.clientHeight]);

  const listOptionsContent = useMemo(() => {
    if (!list.length) {
      return null;
    }

    return list.map((item, key) => {
      return (
        <div
          onClick={() => handleClick(item)}
          className={cx(classes.listItem, { [classes.listItemDisabled]: !!item?.disabled })}
          key={key}
        >
          {renderOption(item)}
        </div>
      );
    });
  }, [classes.listItem, handleClick, list, listAbsoluteStyles, renderOption]);

  const priorityListOptionsContent = useMemo(() => {
    if (!priorityList?.length) {
      return null;
    }

    return (priorityList || []).map((item, key) => {
      return (
        <div
          onClick={() => handleClick(item)}
          className={cx(classes.listItem, { [classes.listItemDisabled]: !!item?.disabled })}
          key={key}
        >
          {renderOption(item)}
        </div>
      );
    });
  }, [classes.listItem, handleClick, priorityList, renderOption]);

  const autocompleteContent = useMemo(() => {
    if (searchValue && (typing || loading) && !withClearIcon) {
      return (
        <div className={cx(classes.loading, { [classes.listAbove]: dropUp })} style={listAbsoluteStyles}>
          {loadingText || t('loading')}...
        </div>
      );
    }

    if (open && searchValue && !typing && !loading && !listOptionsContent && searchValue.length >= minSearchLength) {
      return (
        <div className={cx(classes.notFoundResults, { [classes.listAbove]: dropUp })} style={listAbsoluteStyles}>
          {actionRow && <div className={classes.actionRow}>{actionRow}</div>}
          {!hideEmpty && (
            <Ellipsis
              classes={{ root: classes.notFoundText }}
              text={t('no_results_found_for_quote_xsearchx_quote', { search: searchValue })}
            />
          )}
        </div>
      );
    }

    if (open && listOptionsContent) {
      return (
        <Flex
          autoWidth={false}
          direction="column"
          wrap="nowrap"
          className={cx(classes.list, { [classes.listAbove]: dropUp })}
          style={listAbsoluteStyles}
        >
          {actionRow && <div className={classes.actionRow}>{actionRow}</div>}
          <div className={classes.optionsList}>{listOptionsContent}</div>
        </Flex>
      );
    }

    if (open && priorityListOptionsContent && priorityListWithFocus) {
      return (
        <Flex direction="column" wrap="nowrap" className={classes.list}>
          <div className={classes.optionsList}>{priorityListOptionsContent}</div>
        </Flex>
      );
    }

    if (priorityListOptionsContent && !priorityListWithFocus) {
      return (
        <Flex direction="column" wrap="nowrap" className={classes.list}>
          <div className={classes.optionsList}>{priorityListOptionsContent}</div>
        </Flex>
      );
    }

    return null;
  }, [
    actionRow,
    classes,
    handleClick,
    hideEmpty,
    listAbsoluteStyles,
    listOptionsContent,
    loading,
    open,
    priorityListOptionsContent,
    priorityListWithFocus,
    renderOption,
    typing,
    withClearIcon,
  ]);

  const endAdornment = useMemo(() => {
    if (loading && open && withClearIcon) {
      return <Loading size={20} />;
    }

    if (searchValue && withClearIcon) {
      return <RoundedIcon onClick={handleClearSearch} classes={{ root: classes.clearIcon }} />;
    }

    return null;
  }, [loading, searchValue, open, onClearList, withClearIcon, handleClearSearch]);

  return (
    <div className={classes.root}>
      {label && (
        <FormLabel classes={{ root: classes.labelRoot, error: classes.labelError }} component="legend">
          {label}
          {action}
        </FormLabel>
      )}
      <div className={classes.search} ref={searchContainerRef}>
        <TextField
          ref={textFieldRef}
          disabled={disabled}
          error={error}
          {...inputProps} // TODO: Remove disabled / error props from Autocomplete, in favour of inputProps
          fullWidth
          InputProps={{
            ...inputProps?.InputProps,
            endAdornment: endAdornment,
          }}
          withSearchIcon={withSearchIcon}
          value={searchValue}
          onChange={handleSearchChange}
          onBlur={(evt) => {
            inputProps?.onBlur?.(evt);

            setTimeout(() => {
              if (mountedRef.current) {
                setOpen(false);
              }
            }, 200);
          }}
          onFocus={() => setOpen(true)}
          classes={{
            root: classes.textField,
            input: classes.textFieldInput,
          }}
        />
        {autocompleteContent}
      </div>
    </div>
  );
};

export const Autocomplete = withStyles(styles)(forwardRef(AutocompleteComponent));
